import type { CaseDefinition, CaseId } from "../domain/types/index.js";
import { CASE_SPECS_DEEP } from "./casesDeep.js";
import { CASE_SPECS_CHOICE } from "./casesChoice.js";
import { CASE_SPECS_LABYRINTH } from "./casesLabyrinth.js";
import { CASE_SPECS_PRODUCT_RELEASE } from "./casesProductRelease.js";
import { CASE_SPECS_STABILIZE } from "./casesStabilize.js";
import { CASE_SPECS_FOUNDATION } from "./casesFoundation.js";
import { CASE_SPECS_EXPANSION } from "./casesExpansion.js";
import { CASE_SPECS_FOCUS } from "./casesFocus.js";
import { CASE_SPECS_AUTONOMY } from "./casesAutonomy.js";
import { CASE_SPECS_ENDURANCE } from "./casesEndurance.js";
import { CASE_SPECS_PEAK } from "./casesPeak.js";
import { CASE_SPECS_WIDE } from "./casesWide.js";
import { defineCases } from "./defineCases.js";
import { CASE_SPECS_FORESIGHT } from "./casesForesight.js";
import { CASE_SPECS_RECOVERY } from "./casesRecovery.js";
import { CASE_SPECS_INHERITANCE } from "./casesInheritance.js";
import { CASE_SPECS_GRADUATION } from "./casesGraduation.js";
import { CASE_SPECS_WINDOW_SEAT } from "./casesWindowSeat.js";
import { CASE_SPECS_INVESTMENT } from "./casesInvestment.js";

const CASE_SPECS_STANDARD = {
  "a1-maint": { name: "保守改修", size: "small", required: 10, deadline: 4, flavor: "影響範囲は「たぶん小さい」。", loadTable: "intro" },
  "a1-lp": { name: "LP制作", size: "small", required: 12, deadline: 3, flavor: "公開日は動かない。", loadTable: "small" },
  "a2-add": { name: "軽微な追加開発", size: "small", required: 11, deadline: 4, flavor: "要件は小さい。前提は毎日変わる。", loadTable: "intro" },
  "a2-service": { name: "新規サービス開発", size: "medium", required: 16, deadline: 4, flavor: "昨日の決定が、今日の前提ではない。", loadTable: "volatile" },
  "a3-link": { name: "基幹連携", size: "small", required: 12, deadline: 4, flavor: "外部仕様書は、いつも更新中。", loadTable: "intro" },
  "a3-inquiry": { name: "問い合わせ対応", size: "small", required: 12, deadline: 4, flavor: "返信待ちのボールが増える。", loadTable: "small" },
  "a4-infra": { name: "初期基盤刷新", size: "small", required: 12, deadline: 4, flavor: "止めた瞬間に、全部が見える。", loadTable: "intro" },
  "a4-expand": { name: "既存機能拡張", size: "medium", required: 15, deadline: 5, flavor: "足場は、すでに誰かのコードだ。", loadTable: "medium" },
  "a5-short": { name: "短期改修", size: "small", required: 13, deadline: 4, flavor: "今週中、と言われた。", loadTable: "intro" },
  "a5-release": { name: "リリース対応", size: "medium", required: 15, deadline: 4, flavor: "チェックリストは、まだ増えている。", loadTable: "incident" },
  "b1-client": { name: "新規顧客導入", size: "small", required: 10, deadline: 3, flavor: "初回納品は、信頼の総仕上げ。", loadTable: "intro" },
  "b1-parallel": { name: "複数稼働の並行", size: "medium", required: 16, deadline: 5, flavor: "会議室が、足りない。", loadTable: "medium" },
  "b2-ops": { name: "運用保守", size: "small", required: 14, deadline: 4, flavor: "監視アラートは、休日も鳴る。", loadTable: "small-mix" },
  "b2-link": { name: "基幹連携", size: "medium", required: 22, deadline: 5, flavor: "接続先の担当者が、また変わった。", loadTable: "volatile" },
  "b3-feature": { name: "新機能開発", size: "medium", required: 20, deadline: 4, flavor: "仕様凍結日が、まだ凍っていない。", loadTable: "volatile" },
  "b3-change": { name: "仕様変更対応", size: "medium", required: 21, deadline: 5, flavor: "変更理由は、毎回「緊急」。", loadTable: "incident" },
  "b4-platform": { name: "全社基盤刷新", size: "large", required: 20, deadline: 6, flavor: "全部門が、同時に困る。", loadTable: "large" },
  "b4-large": { name: "大型改修", size: "medium", required: 18, deadline: 5, flavor: "影響調査だけで、一週間。", loadTable: "medium" },
  "b5-migration": { name: "運用移行", size: "medium", required: 20, deadline: 4, flavor: "ロールバック手順は、祈りである。", loadTable: "standard" },
  "b5-delivery": { name: "納品対応", size: "medium", required: 22, deadline: 5, flavor: "顧客レビューは、明日から。", loadTable: "incident" },
  "c1-overhaul": { name: "大規模改修", size: "medium", required: 20, deadline: 5, flavor: "レガシーは、静かに抵抗する。", loadTable: "medium" },
  "c1-emergency": { name: "緊急プロジェクト", size: "large", required: 23, deadline: 6, flavor: "社長直下、と言われた。", loadTable: "large" },
  "c2-debt": { name: "技術負債返済", size: "small", required: 15, deadline: 4, flavor: "借金の利息が、日に日に増える。", loadTable: "small" },
  "c2-demand": { name: "顧客要求対応", size: "medium", required: 21, deadline: 5, flavor: "要望リストは、終わらない。", loadTable: "medium" },
  "c3-release": { name: "高難度リリース", size: "large", required: 24, deadline: 6, flavor: "本番反映は、深夜2時。", loadTable: "large" },
  "c3-incident": { name: "障害対応", size: "medium", required: 20, deadline: 5, flavor: "原因は、まだ不明。", loadTable: "medium" },
  "c4-strategy": { name: "戦略テーマ", size: "large", required: 23, deadline: 5, flavor: "3年後の数字が、今週必要。", loadTable: "large" },
  "c4-final": { name: "最終調整", size: "medium", required: 21, deadline: 5, flavor: "最後の1%が、半分を占める。", loadTable: "medium" },
  "c5-preboss-a": { name: "ボス前リリース", size: "medium", required: 23, deadline: 5, flavor: "本番前の静けさ。", loadTable: "standard" },
  "c5-preboss-b": { name: "ボス前リリース", size: "medium", required: 18, deadline: 4, flavor: "チェック項目が、まだ残っている。", loadTable: "medium" },
  "standard-boss": { name: "全社基幹システム更改", size: "boss", required: 38, deadline: 6, flavor: "これを落とすと、会社が終わる。", loadTable: "boss", isBoss: true },
  "hard-boss": { name: "障害復旧を伴う本番切替", size: "boss", required: 42, deadline: 6, flavor: "切り替えるたび、別の警報が鳴る。", loadTable: "boss", isBoss: true },
} as const;

export const CASES = defineCases({
  ...CASE_SPECS_STANDARD,
  ...CASE_SPECS_WIDE,
  ...CASE_SPECS_DEEP,
  ...CASE_SPECS_PEAK,
  ...CASE_SPECS_CHOICE,
  ...CASE_SPECS_LABYRINTH,
  ...CASE_SPECS_PRODUCT_RELEASE,
  ...CASE_SPECS_STABILIZE,
  ...CASE_SPECS_FOUNDATION,
  ...CASE_SPECS_EXPANSION,
  ...CASE_SPECS_FOCUS,
  ...CASE_SPECS_AUTONOMY,
  ...CASE_SPECS_ENDURANCE,
  ...CASE_SPECS_FORESIGHT,
  ...CASE_SPECS_RECOVERY,
  ...CASE_SPECS_INHERITANCE,
  ...CASE_SPECS_GRADUATION,
  ...CASE_SPECS_WINDOW_SEAT,
  ...CASE_SPECS_INVESTMENT,
}) satisfies Record<CaseId, CaseDefinition>;
